import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { BRANCH_LABELS } from '@lorsamthai/ops-shared';
import { listBranchRequests, type BranchRequestDoc } from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtDateTime } from '../lib/format.js';
import {
  REQUEST_KIND_ICONS,
  REQUEST_KIND_LABELS,
  REQUEST_STATUS_LABELS,
  REQUEST_STATUS_TONES,
  URGENCY_LABELS,
  URGENCY_TONES,
} from '../lib/requestLabels.js';

type StatusFilter = 'open' | 'closed' | 'all';

export default function RequestList() {
  const [sp, setSp] = useSearchParams();
  const status = (sp.get('status') as StatusFilter) || 'open';
  const [items, setItems] = useState<BranchRequestDoc[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    listBranchRequests({ status })
      .then((r) => {
        setItems(r.items);
        setTotal(r.total);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'load failed'))
      .finally(() => setLoading(false));
  }, [status]);

  const setStatus = (s: StatusFilter) => {
    const next = new URLSearchParams(sp);
    next.set('status', s);
    setSp(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">คำขอ (Requests)</h1>
          <p className="mt-1 text-sm text-slate-500">
            ส่งคำขอโอน · ขอสั่งเพิ่ม · ปรับ stock · ปรับ target · แจ้ง demand
          </p>
        </div>
        <Link
          to="/requests/new"
          className="shrink-0 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white"
        >
          + สร้างคำขอ
        </Link>
      </div>

      <div className="flex gap-2 text-sm">
        {(['open', 'closed', 'all'] as StatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={
              'rounded-md border px-3 py-1.5 ' +
              (status === s
                ? 'border-slate-900 bg-slate-900 text-white'
                : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
            }
          >
            {s === 'open' ? 'ค้างรอตรวจ' : s === 'closed' ? 'ปิดแล้ว' : 'ทั้งหมด'}
          </button>
        ))}
      </div>

      {error && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      {loading ? (
        <div className="text-sm text-slate-500">กำลังโหลด...</div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          ไม่มีคำขอใน{' '}
          {status === 'open' ? 'คิวรอตรวจ' : status === 'closed' ? 'รายการปิดแล้ว' : 'ระบบ'}
        </div>
      ) : (
        <ul className="divide-y divide-slate-100 overflow-hidden rounded-xl border border-slate-200 bg-white">
          {items.map((r) => {
            const totalQty = r.branch_request_items.reduce((s, i) => s + i.qty, 0);
            return (
              <li key={r.id}>
                <Link to={`/requests/${r.id}`} className="block px-4 py-3 hover:bg-slate-50">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-base">{REQUEST_KIND_ICONS[r.kind]}</span>
                        <span className="font-semibold">{REQUEST_KIND_LABELS[r.kind]}</span>
                        <span className="font-mono text-xs text-slate-500">
                          {r.req_ref ?? `#${r.id}`}
                        </span>
                        {r.urgency !== 'normal' && (
                          <Badge tone={URGENCY_TONES[r.urgency]}>{URGENCY_LABELS[r.urgency]}</Badge>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-slate-500">
                        จาก <strong>{BRANCH_LABELS[r.from_branch]}</strong>
                        {r.source_branch && (
                          <> · ขอจาก <strong>{BRANCH_LABELS[r.source_branch]}</strong></>
                        )}
                        {' · '}
                        {r.branch_request_items.length} SKU · รวม {totalQty} เส้น
                      </div>
                      {r.reason && (
                        <p className="mt-1 line-clamp-2 text-xs text-slate-500">{r.reason}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <Badge tone={REQUEST_STATUS_TONES[r.status]}>
                        {REQUEST_STATUS_LABELS[r.status]}
                      </Badge>
                      <div className="mt-1 text-xs text-slate-400">
                        {fmtDateTime(r.submitted_at)}
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && items.length > 0 && (
        <p className="text-center text-xs text-slate-400">{total} รายการ</p>
      )}
    </div>
  );
}
